import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {FetchPolicy} from "@khanacademy/wonder-blocks-data";
import {
    enums,
    useMarkConversion,
    withConversionContext,
    type PartialLearningTimeInfo,
} from "@khan/analytics-learning-time";
import {PageLifecycle} from "@khan/analytics-performance";
import {loggedInRedirect} from "@khan/auth-login";
import {
    PRODUCT_BY_ID_QUERY,
    type Referral,
    type Role,
    type Step,
} from "@khan/auth-shared";
import {useQuery} from "@khan/data-gql";
import {PermissionsInfo, RequestInfo} from "@khan/shared-core";
import {mediaQueries} from "@khan/shared-styles";
import {cssUrl, isKhanAcademyUrl, useLocation} from "@khan/urls";

import PageRoot from "#app-shell-package/page-root.tsx";
import TermsOfServiceSignupStatement from "#auth/components/terms-of-service-signup-statement.tsx";
import {isKhanmigoReferral} from "#auth/components/util.ts";
import {KhanmigoHeader} from "#khanmigo-package/khanmigo-header.tsx";
import {
    amountToDollars,
    khanmigoStripeProduct,
} from "#shared/auth/signup/signup-detail-screen.tsx";

import ProgressShellDetailScreen from "../progress-shell/progress-shell-detail-screen.tsx";
import ProgressShellFull from "../progress-shell/progress-shell-full.tsx";
import type ProgressShellWithBackground from "../progress-shell/progress-shell-with-background.tsx";
import {getReferralType} from "../signup/signup-page.tsx";

import LoginActionScreen from "./login-action-screen.tsx";

function LoginPage() {
    const markConversion = useMarkConversion();
    const location = useLocation();
    const referral = getReferralType(location);
    const {queryParams} = location;
    const [monthlyCost, setMonthlyCost] = React.useState("");
    const [yearlyCost, setYearlyCost] = React.useState("");

    React.useEffect(() => {
        PageLifecycle.reportPageLifecycleTiming("login_page", {
            interactive: true,
            usable: true,
        });
        markConversion({id: "pageview_login"});
    }, [markConversion]);

    // @ts-expect-error - TS2339 - Property 'data' does not exist on type 'Result<{ data }>'.
    const [{status, data}] = useQuery(PRODUCT_BY_ID_QUERY, {
        variables: {id: khanmigoStripeProduct},
        fetchPolicy: FetchPolicy.CacheAndNetwork,
        skip: !isKhanmigoReferral(referral),
    });
    if (status === "loading") {
        return null;
    }

    // NOTE: normally we'd handle the error here. But instead of showing a
    // "sorry..." message, we're updating the copy to just not include price.

    if (status === "success" && data.product?.prices) {
        if (monthlyCost === "") {
            const prices = data.product.prices.filter((price) => price != null);
            prices.sort((a, b) => a.unitAmount - b.unitAmount);
            setMonthlyCost(amountToDollars(prices[0].unitAmount));
            setYearlyCost(amountToDollars(prices[1].unitAmount));
        }
    }

    const continueUrl = isKhanAcademyUrl(queryParams.continue || "")
        ? queryParams.continue
        : "/";

    // If the user is already logged in, redirect them to continue URL or
    // the homepage if there is none.
    if (PermissionsInfo.isLoggedIn) {
        return loggedInRedirect(continueUrl);
    }

    function getInitialRole(): Role {
        const {queryParams} = location;
        const initialRole = queryParams["initial_role"];
        if (initialRole === "teacher" || initialRole === "parent") {
            return initialRole;
        }
        // default to learner
        return "learner";
    }

    function isForZendeskLogin(): boolean {
        const {queryParams} = location;
        return queryParams.purpose === "zendesk";
    }

    function getReferralStyle(referral: Referral) {
        const referralStyles = [styles.loginDetails];
        if (isKhanmigoReferral(referral)) {
            referralStyles.push(styles.khanmigoLoginDetails);
        }

        return referralStyles;
    }

    const detailScreen = () => (
        <View style={getReferralStyle(referral)}>
            {isForZendeskLogin() ? (
                <ProgressShellDetailScreen
                    heading={t`To continue to the Help Center, log in to Khan Academy`}
                    detailContent={
                        <TermsOfServiceSignupStatement purpose="login" />
                    }
                    fontColor={isKhanmigoReferral(referral) ? "dark" : "light"}
                    newLayout={true}
                />
            ) : (
                <ProgressShellDetailScreen
                    heading={getHeading(referral)}
                    optionalDetailItems={[
                        getSubHeading(referral, monthlyCost, yearlyCost),
                    ]}
                    detailContent={
                        <TermsOfServiceSignupStatement purpose="login" />
                    }
                    fontColor={isKhanmigoReferral(referral) ? "dark" : "light"}
                    newLayout={true}
                />
            )}
        </View>
    );

    const actionScreen: JSX.LibraryManagedAttributes<
        typeof ProgressShellWithBackground,
        React.ComponentProps<typeof ProgressShellWithBackground>
    >["actionScreen"] = (
        step: Step,
        updateStep: JSX.LibraryManagedAttributes<
            typeof LoginActionScreen,
            React.ComponentProps<typeof LoginActionScreen>
        >["updateStep"],
    ): React.ReactNode => (
        <LoginActionScreen
            continueUrl={continueUrl}
            role={getInitialRole()}
            updateStep={updateStep}
        />
    );

    return (
        <PageRoot
            settings={{
                // If the user is coming from Khanmigo, we don't want
                // to show the standard header, we'll instead show a
                // custom one.
                // Also, hide the header if we're in the extension sidepanel.
                header:
                    !RequestInfo.isExtension && !isKhanmigoReferral(referral),
                // Hide the footer if we're in the extension sidepanel.
                footer: !RequestInfo.isExtension,
            }}
        >
            {isKhanmigoReferral(referral) && <KhanmigoHeader />}
            <ProgressShellFull
                referral={referral}
                initialStep="login"
                detailScreen={detailScreen}
                actionScreen={actionScreen}
            />
        </PageRoot>
    );
}

function getHeading(referral: string): string {
    // Remove this once we're fully using KhanmigoSignup referral
    if (referral === "KhanmigoCheckout") {
        return t`Ready to get AI-powered tutoring?`;
    }
    if (referral === "KhanmigoSignup") {
        return t`Log in to your Khan Academy account`;
    }
    if (referral === "DistrictTrials") {
        return t`Log in now to accept the District Trials terms of service!`;
    }

    return t`Join Khan Academy and learn with us`;
}

function getSubHeading(
    referral: string,
    monthlyCost: string,
    yearlyCost: string,
): string {
    // Remove this once we're fully using KhanmigoSignup referral
    if (referral === "KhanmigoCheckout") {
        if (monthlyCost && yearlyCost) {
            return t`Get access to Khanmigo’s personalized learning for $${monthlyCost}/month or $${yearlyCost}/year and level up your learning today.`;
        }
        // Otherwise fallback to the generic message
        return t`Get access to Khanmigo’s personalized learning and level up your learning today.`;
    }
    if (referral === "KhanmigoSignup") {
        return t`Finish signing up for Khanmigo in seconds.`;
    }

    if (referral === "DistrictTrials") {
        return t`Finish signing up in seconds.`;
    }

    return t`Log in to Khan Academy to get started!`;
}

const styles = StyleSheet.create({
    loginDetails: {
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",

        [mediaQueries.lgOrLarger]: {
            paddingTop: 320,
            width: "100%",
            maxWidth: 443,
            backgroundSize: "100%",
            backgroundImage: cssUrl("/images/signup/signup-banner-2x.png"),
            backgroundRepeat: "no-repeat",
            backgroundPosition: "top center",
        },
        [mediaQueries.xlOrLarger]: {
            paddingTop: 360,
        },
    },
    khanmigoLoginDetails: {
        [mediaQueries.lgOrLarger]: {
            paddingTop: 100,
            backgroundSize: 104,
            backgroundImage: cssUrl("/images/khanmigo/winking_khanmigo-2x.png"),
            backgroundPosition: "top center",
        },
    },
});

const mapPropsToLearningTime = (): PartialLearningTimeInfo => {
    return {
        activity: enums.Activity.OTHER_ACTIVITY,
    };
};

type ExportProps = React.ComponentProps<typeof LoginPage>;

export default withConversionContext(mapPropsToLearningTime)(
    LoginPage,
) as React.ComponentType<ExportProps>;
